import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Company, CompanyCompetence, CompanyCompetenceValues } from "models/Company";
import AddressPickerDialog from "features/maps/AddressPickerDialog";
import MultiSelect from "components/MultiSelect";
import { CreateCompanyRequest } from "api/companies/createCompany";
import { GenericError } from "interfaces/errorTypes";
import { useCreateCompanyMutation, useUpdateCompanyMutation } from "app/api/companies";
import MyLocation from "@mui/icons-material/MyLocation";
import Check from "@mui/icons-material/Check";
import Clear from "@mui/icons-material/Clear";
import { formatAddress } from "features/maps/util/formatAddress";

type CompanyFormProps = {
  companyToEdit?: Company;
  onClose?: () => void;
  onSuccess?: () => void;
};

type FormErrors = {
  address?: { type: string; message: string };
};

const CompanyForm: React.FC<CompanyFormProps> = (props) => {
  const { companyToEdit, onClose, onSuccess } = props;

  const { hasRole } = useAuthContext();
  const canEditCompanies = hasRole([UserRole.ADMIN, UserRole.USER_ADMIN]);

  const [createCompany] = useCreateCompanyMutation();
  const [updateCompany] = useUpdateCompanyMutation();
  const [serverError, setServerError] = useState<GenericError | null>(null);

  const [showAddressPickerDialog, setShowAddressPickerDialog] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    setValue,
    watch,
  } = useForm<CreateCompanyRequest>({
    reValidateMode: "onSubmit",
    defaultValues: {
      name: companyToEdit?.name || "",
      ueCode: companyToEdit?.ueCode || "",
      orgNr: companyToEdit?.orgNr || "",
      contactName: companyToEdit?.contactName || "",
      phone: companyToEdit?.phone || "",
      email: companyToEdit?.email || "",
      bookingEmail: companyToEdit?.bookingEmail || "",
      address: companyToEdit?.address || "",
      postalCode: companyToEdit?.postalCode || "",
      city: companyToEdit?.city || "",
      latitude: companyToEdit?.latitude,
      longitude: companyToEdit?.longitude,
      region: companyToEdit?.region || "",
      hourlyRate: companyToEdit?.hourlyRate || null,
      rating: companyToEdit?.rating || null,
      ratingNotApplicable: companyToEdit?.ratingNotApplicable || false,
      kammarkollegietApproved: companyToEdit?.kammarkollegietApproved || false,
      listedIn5620: companyToEdit?.listedIn5620 || false,
      folderUrl: companyToEdit?.folderUrl || "",
      competences: companyToEdit?.competences || [],
    },
    validate: ({ formValues }): FormErrors | true => {
      if (
        !formValues.address ||
        !formValues.postalCode ||
        !formValues.city ||
        !formValues.latitude ||
        !formValues.longitude
      ) {
        return {
          address: { type: "custom", message: "Ofullständig adress" },
        };
      }
      return true;
    },
  });

  const ratingNotApplicable = watch("ratingNotApplicable");

  const onSubmit: SubmitHandler<CreateCompanyRequest> = (data) => {
    if (!data.address || !data.postalCode || !data.city || !data.latitude || !data.longitude) {
      setError("address", { type: "custom", message: "Ofullständig adress" });
    }

    setServerError(null);
    (companyToEdit ? updateCompany({ id: companyToEdit.id, ...data }) : createCompany(data))
      .unwrap()
      .then(() => {
        toast.success(`Företaget har ${companyToEdit ? "uppdaterats" : "lagts till"}!`, {
          autoClose: 3000,
          hideProgressBar: true,
        });
        onSuccess?.();
      })
      .catch((error) => {
        setServerError({ message: error.data || "Ett okänt fel uppstod." });
      });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <Grid container spacing={2} mb={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1}>
              <FormGroup>
                <FormLabel>Identitet</FormLabel>

                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Företagsnamn"
                      type="text"
                      required
                      autoComplete="new-password"
                      variant="filled"
                      fullWidth
                      sx={{ mb: 1 }}
                      disabled={!canEditCompanies}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />

                <Controller
                  name="ueCode"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) =>
                      RegExp(/^[0-9A-ZÅÄÖ\-_/\s&]+$/).test(value) ||
                      "Leverantörskoden innehåller ogiltiga tecken",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Leverantörskod (Pyramid)"
                      type="text"
                      required
                      autoComplete="new-password"
                      variant="filled"
                      fullWidth
                      sx={{ mb: 1 }}
                      disabled={!canEditCompanies}
                      error={!!errors.ueCode}
                      helperText={errors.ueCode?.message}
                    />
                  )}
                />

                <Controller
                  name="orgNr"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) =>
                      RegExp(/^[0-9]{6}-[0-9]{4}$/).test(value) ||
                      "Org.nummer får endast innehålla siffror i formatet XXXXXX-XXXX",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Org.nummer"
                      placeholder="XXXXXX-XXXX"
                      type="text"
                      required
                      autoComplete="new-password"
                      variant="filled"
                      fullWidth
                      disabled={!canEditCompanies}
                      error={!!errors.orgNr}
                      helperText={errors.orgNr?.message}
                    />
                  )}
                />
              </FormGroup>

              <FormControl>
                <FormLabel required>Adress</FormLabel>

                {(watch("address") || watch("postalCode") || watch("city")) && (
                  <Typography>
                    {formatAddress({
                      address: watch("address"),
                      postalCode: watch("postalCode"),
                      city: watch("city"),
                    })}
                  </Typography>
                )}

                {watch("latitude") && watch("longitude") && (
                  <Typography fontSize="small" color="text.secondary">
                    Lat, Lng: {watch("latitude")}, {watch("longitude")}
                  </Typography>
                )}

                {!!(errors.form as FormErrors)?.address && (
                  <FormHelperText error>
                    {(errors.form as FormErrors)?.address?.message}
                  </FormHelperText>
                )}

                <Button
                  variant="outlined"
                  onClick={() => setShowAddressPickerDialog(true)}
                  startIcon={<MyLocation />}
                  disabled={!canEditCompanies}
                  sx={{ mt: 1 }}
                >
                  Välj på karta
                </Button>

                {showAddressPickerDialog && (
                  <AddressPickerDialog
                    initialAddress={{
                      address: watch("address"),
                      postalCode: watch("postalCode"),
                      city: watch("city"),
                      latitude: watch("latitude"),
                      longitude: watch("longitude"),
                    }}
                    onSelectAddress={(address) => {
                      setValue("address", address.address, { shouldDirty: true });
                      setValue("postalCode", address.postalCode, { shouldDirty: true });
                      setValue("city", address.city, { shouldDirty: true });
                      setValue("latitude", address.latitude || 0, { shouldDirty: true });
                      setValue("longitude", address.longitude || 0, { shouldDirty: true });
                      setShowAddressPickerDialog(false);
                    }}
                    onClose={() => setShowAddressPickerDialog(false)}
                  />
                )}
              </FormControl>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormGroup>
              <FormLabel>Kontakt</FormLabel>

              <Controller
                name="contactName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Namn"
                    type="text"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    sx={{ mb: 1 }}
                    disabled={!canEditCompanies}
                    error={!!errors.contactName}
                    helperText={errors.contactName?.message}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefonnummer"
                    type="text"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    sx={{ mb: 1 }}
                    disabled={!canEditCompanies}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-post"
                    type="email"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    sx={{ mb: 1 }}
                    disabled={!canEditCompanies}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />

              <Controller
                name="bookingEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="E-post (bokning)"
                    type="email"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    disabled={!canEditCompanies}
                    error={!!errors.bookingEmail}
                    helperText={errors.bookingEmail?.message}
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormGroup>
              <FormLabel>Egenskaper</FormLabel>

              <Controller
                name="hourlyRate"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Timpris"
                    type="number"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    sx={{ mb: 1 }}
                    disabled={!canEditCompanies}
                    error={!!errors.hourlyRate}
                    helperText={errors.hourlyRate?.message}
                  />
                )}
              />

              <Controller
                name="region"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Arbetsområde"
                    placeholder="t.ex. Stockholm, Skåne, Södra Sverige"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    sx={{ mb: 1 }}
                    disabled={!canEditCompanies}
                    error={!!errors.region}
                    helperText={errors.region?.message}
                  />
                )}
              />

              <Controller
                name="competences"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    {...field}
                    label="Kompetenser"
                    sx={{ mb: 1 }}
                    disabled={!canEditCompanies}
                    options={Object.entries(CompanyCompetenceValues).map(([key, label]) => ({
                      id: key as CompanyCompetence,
                      name: label,
                    }))}
                  />
                )}
              />
            </FormGroup>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormGroup>
              <FormLabel>Ekonomi</FormLabel>

              <Controller
                name="rating"
                control={control}
                rules={{
                  validate: (value) =>
                    value === null ||
                    (value >= 1 && value <= 100) ||
                    "Rating måste vara mellan 1 och 100",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Kredit-rating"
                    type="number"
                    placeholder="1-100"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    disabled={ratingNotApplicable || !canEditCompanies}
                    error={!!errors.rating}
                    helperText={errors.rating?.message}
                  />
                )}
              />

              <Controller
                name="ratingNotApplicable"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Rating ej tillämpligt (t.ex. egenföretagare)"
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={!canEditCompanies}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                  />
                )}
              />

              <Controller
                name="folderUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="SharePoint-mapp"
                    type="url"
                    autoComplete="new-password"
                    variant="filled"
                    fullWidth
                    disabled={!canEditCompanies}
                    error={!!errors.folderUrl}
                    helperText={errors.folderUrl?.message}
                  />
                )}
              />

              <Controller
                name="listedIn5620"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Finns i 5620"
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={!canEditCompanies}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                  />
                )}
              />

              <Controller
                name="kammarkollegietApproved"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    label="Godkänd av Kammarkollegiet"
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        disabled={!canEditCompanies}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>

        {serverError && (
          <Alert severity="error">
            <p className="m-0">{serverError.message}</p>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose} startIcon={<Clear />}>
          Avbryt
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={!isDirty}
          hidden={!canEditCompanies}
          startIcon={<Check />}
        >
          Spara
        </Button>
      </DialogActions>
    </Box>
  );
};

export default CompanyForm;
